import * as i0 from '@angular/core';
import { Injectable, InjectionToken, inject, input, output, computed, signal, effect, afterRenderEffect, untracked, Component, Directive, NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { createMotion, resolveDuration } from '@primeuix/motion';
import { nextFrame } from '@primeuix/utils';
import { BaseComponent, PARENT_INSTANCE } from 'primeng/basecomponent';
import * as i1 from 'primeng/bind';
import { Bind, BindModule } from 'primeng/bind';
import { BaseStyle } from 'primeng/base';
const _c0 = ["*"];
function Motion_Conditional_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵprojection(0);
  }
}
const originalStyles = new WeakMap();
function applyHiddenStyles(element, strategy) {
  if (!element) return;
  if (!originalStyles.has(element)) {
    originalStyles.set(element, {
      display: element.style.display,
      visibility: element.style.visibility,
      maxHeight: element.style.maxHeight
    });
  }
  switch (strategy) {
    case 'display':
      element.style.display = 'none';
      break;
    case 'visibility':
      element.style.visibility = 'hidden';
      element.style.maxHeight = '0';
      break;
  }
}
function resetStyles(element, strategy) {
  if (!element) return;
  const original = originalStyles.get(element) ?? element.style;
  switch (strategy) {
    case 'display':
      element.style.display = original?.display || '';
      break;
    case 'visibility':
      element.style.visibility = original?.visibility || '';
      element.style.maxHeight = original?.maxHeight || '';
      break;
  }
  originalStyles.delete(element);
}
const style = /*css*/`
    .p-motion {
        display: block;
    }
`;
const classes = {
  root: 'p-motion'
};
let MotionStyle = /*#__PURE__*/(() => {
  class MotionStyle extends BaseStyle {
    name = 'motion';
    style = style;
    classes = classes;
    static ɵfac = /* @__PURE__ */(() => {
      let ɵMotionStyle_BaseFactory;
      return function MotionStyle_Factory(__ngFactoryType__) {
        return (ɵMotionStyle_BaseFactory || (ɵMotionStyle_BaseFactory = i0.ɵɵgetInheritedFactory(MotionStyle)))(__ngFactoryType__ || MotionStyle);
      };
    })();
    static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: MotionStyle,
      factory: MotionStyle.ɵfac
    });
  }
  return MotionStyle;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
/**
 *
 * Motion and MotionDirective provide an easy way to add motion effects to Angular applications.
 *
 * [Live Demo](https://www.primeng.org/motion)
 *
 * @module motionstyle
 *
 */
var MotionClasses = /*#__PURE__*/function (MotionClasses) {
  /**
   * Class name of the root element
   */
  MotionClasses["root"] = "p-motion";
  return MotionClasses;
}(MotionClasses || {});
const MOTION_INSTANCE = /*#__PURE__*/new InjectionToken('MOTION_INSTANCE');
/**
 * Motion component is a container to apply motion effects to its content.
 * @group Components
 */
let Motion = /*#__PURE__*/(() => {
  class Motion extends BaseComponent {
    $pcMotion = inject(MOTION_INSTANCE, {
      optional: true,
      skipSelf: true
    }) ?? undefined;
    bindDirectiveInstance = inject(Bind, {
      self: true
    });
    onAfterViewChecked() {
      const options = this.options();
      const optionsAttrs = options?.root || {};
      this.bindDirectiveInstance.setAttrs({
        ...this.ptms(['host', 'root']),
        ...optionsAttrs
      });
    }
    _componentStyle = inject(MotionStyle);
    /******************** Inputs ********************/
    /**
     * Whether the element is visible or not.
     * @group Props
     */
    visible = input(false, ...(ngDevMode ? [{
      debugName: "visible"
    }] : []));
    /**
     * Whether to mount the element on enter.
     * @group Props
     */
    mountOnEnter = input(true, ...(ngDevMode ? [{
      debugName: "mountOnEnter"
    }] : []));
    /**
     * Whether to unmount the element on leave.
     * @group Props
     */
    unmountOnLeave = input(true, ...(ngDevMode ? [{
      debugName: "unmountOnLeave"
    }] : []));
    /**
     * The name of the motion. It can be a predefined motion name or a custom one.
     * phases:
     *     [name]-enter
     *     [name]-enter-active
     *     [name]-enter-to
     *     [name]-leave
     *     [name]-leave-active
     *     [name]-leave-to
     * @group Props
     */
    name = input(undefined, ...(ngDevMode ? [{
      debugName: "name"
    }] : []));
    /**
     * The type of the motion, valid values 'transition' and 'animation'.
     * @group Props
     */
    type = input(undefined, ...(ngDevMode ? [{
      debugName: "type"
    }] : []));
    /**
     * Whether the motion is safe.
     * @group Props
     */
    safe = input(undefined, ...(ngDevMode ? [{
      debugName: "safe"
    }] : []));
    /**
     * Whether the motion is disabled.
     * @group Props
     */
    disabled = input(false, ...(ngDevMode ? [{
      debugName: "disabled"
    }] : []));
    /**
     * Whether the motion should appear.
     * @group Props
     */
    appear = input(false, ...(ngDevMode ? [{
      debugName: "appear"
    }] : []));
    /**
     * Whether the motion should enter.
     * @group Props
     */
    enter = input(true, ...(ngDevMode ? [{
      debugName: "enter"
    }] : []));
    /**
     * Whether the motion should leave.
     * @group Props
     */
    leave = input(true, ...(ngDevMode ? [{
      debugName: "leave"
    }] : []));
    /**
     * The duration of the motion.
     * @group Props
     */
    duration = input(undefined, ...(ngDevMode ? [{
      debugName: "duration"
    }] : []));
    /**
     * The hide strategy of the motion, valid values 'display' and 'visibility'.
     * @group Props
     */
    hideStrategy = input('display', ...(ngDevMode ? [{
      debugName: "hideStrategy"
    }] : []));
    /**
     * The enter from class of the motion.
     * @group Props
     */
    enterFromClass = input(undefined, ...(ngDevMode ? [{
      debugName: "enterFromClass"
    }] : []));
    /**
     * The enter to class of the motion.
     * @group Props
     */
    enterToClass = input(undefined, ...(ngDevMode ? [{
      debugName: "enterToClass"
    }] : []));
    /**
     * The enter active class of the motion.
     * @group Props
     */
    enterActiveClass = input(undefined, ...(ngDevMode ? [{
      debugName: "enterActiveClass"
    }] : []));
    /**
     * The leave from class of the motion.
     * @group Props
     */
    leaveFromClass = input(undefined, ...(ngDevMode ? [{
      debugName: "leaveFromClass"
    }] : []));
    /**
     * The leave to class of the motion.
     * @group Props
     */
    leaveToClass = input(undefined, ...(ngDevMode ? [{
      debugName: "leaveToClass"
    }] : []));
    /**
     * The leave active class of the motion.
     * @group Props
     */
    leaveActiveClass = input(undefined, ...(ngDevMode ? [{
      debugName: "leaveActiveClass"
    }] : []));
    /******************** All Inputs ********************/
    /**
     * The motion options.
     * @group Props
     */
    options = input({}, ...(ngDevMode ? [{
      debugName: "options"
    }] : []));
    /******************** Outputs ********************/
    /**
     * Callback fired before the enter transition/animation starts.
     * @param {MotionEvent} [event] - The event object containing details about the motion.
     * @param {Element} event.element - The element being transitioned/animated.
     * @group Emits
     */
    onBeforeEnter = output();
    /**
     * Callback fired when the enter transition/animation starts.
     * @param {MotionEvent} [event] - The event object containing details about the motion.
     * @param {Element} event.element - The element being transitioned/animated.
     * @group Emits
     */
    onEnter = output();
    /**
     * Callback fired after the enter transition/animation ends.
     * @param {MotionEvent} [event] - The event object containing details about the motion.
     * @param {Element} event.element - The element being transitioned/animated.
     * @group Emits
     */
    onAfterEnter = output();
    /**
     * Callback fired when the enter transition/animation is cancelled.
     * @param {MotionEvent} [event] - The event object containing details about the motion.
     * @param {Element} event.element - The element being transitioned/animated.
     * @group Emits
     */
    onEnterCancelled = output();
    /**
     * Callback fired before the leave transition/animation starts.
     * @param {MotionEvent} [event] - The event object containing details about the motion.
     * @param {Element} event.element - The element being transitioned/animated.
     * @group Emits
     */
    onBeforeLeave = output();
    /**
     * Callback fired when the leave transition/animation starts.
     * @param {MotionEvent} [event] - The event object containing details about the motion.
     * @param {Element} event.element - The element being transitioned/animated.
     * @group Emits
     */
    onLeave = output();
    /**
     * Callback fired after the leave transition/animation ends.
     * @param {MotionEvent} [event] - The event object containing details about the motion.
     * @param {Element} event.element - The element being transitioned/animated.
     * @group Emits
     */
    onAfterLeave = output();
    /**
     * Callback fired when the leave transition/animation is cancelled.
     * @param {MotionEvent} [event] - The event object containing details about the motion.
     * @param {Element} event.element - The element being transitioned/animated.
     * @group Emits
     */
    onLeaveCancelled = output();
    /******************** Computed ********************/
    motionOptions = computed(() => {
      const options = this.options();
      return {
        name: options.name ?? this.name(),
        type: options.type ?? this.type(),
        safe: options.safe ?? this.safe(),
        disabled: options.disabled ?? this.disabled(),
        appear: false,
        enter: options.enter ?? this.enter(),
        leave: options.leave ?? this.leave(),
        duration: options.duration ?? this.duration(),
        enterClass: {
          from: options.enterClass?.from ?? (!options.name ? this.enterFromClass() : undefined),
          to: options.enterClass?.to ?? (!options.name ? this.enterToClass() : undefined),
          active: options.enterClass?.active ?? (!options.name ? this.enterActiveClass() : undefined)
        },
        leaveClass: {
          from: options.leaveClass?.from ?? (!options.name ? this.leaveFromClass() : undefined),
          to: options.leaveClass?.to ?? (!options.name ? this.leaveToClass() : undefined),
          active: options.leaveClass?.active ?? (!options.name ? this.leaveActiveClass() : undefined)
        },
        onBeforeEnter: options.onBeforeEnter ?? this.handleBeforeEnter,
        onEnter: options.onEnter ?? this.handleEnter,
        onAfterEnter: options.onAfterEnter ?? this.handleAfterEnter,
        onEnterCancelled: options.onEnterCancelled ?? this.handleEnterCancelled,
        onBeforeLeave: options.onBeforeLeave ?? this.handleBeforeLeave,
        onLeave: options.onLeave ?? this.handleLeave,
        onAfterLeave: options.onAfterLeave ?? this.handleAfterLeave,
        onLeaveCancelled: options.onLeaveCancelled ?? this.handleLeaveCancelled
      };
    }, ...(ngDevMode ? [{
      debugName: "motionOptions"
    }] : []));
    motion;
    isInitialMount = true;
    cancelled = false;
    destroyed = false;
    rendered = signal(false, ...(ngDevMode ? [{
      debugName: "rendered"
    }] : []));
    handleBeforeEnter = event => !this.destroyed && this.onBeforeEnter.emit(event);
    handleEnter = event => !this.destroyed && this.onEnter.emit(event);
    handleAfterEnter = event => !this.destroyed && this.onAfterEnter.emit(event);
    handleEnterCancelled = event => !this.destroyed && this.onEnterCancelled.emit(event);
    handleBeforeLeave = event => !this.destroyed && this.onBeforeLeave.emit(event);
    handleLeave = event => !this.destroyed && this.onLeave.emit(event);
    handleAfterLeave = event => !this.destroyed && this.onAfterLeave.emit(event);
    handleLeaveCancelled = event => !this.destroyed && this.onLeaveCancelled.emit(event);
    constructor() {
      super();
      effect(() => {
        const hideStrategy = this.hideStrategy();
        if (this.isInitialMount) {
          applyHiddenStyles(this.$el, hideStrategy);
          this.rendered.set(this.visible() && this.mountOnEnter() || !this.mountOnEnter());
        } else if (this.visible() && !this.rendered()) {
          applyHiddenStyles(this.$el, hideStrategy);
          this.rendered.set(true);
        }
      });
      effect(() => {
        if (!this.motion) {
          this.motion = createMotion(this.$el, this.motionOptions());
        } else {
          // @todo: Update motion options method to update options dynamically
          //this.motion.update(this.$el, this.motionOptions());
        }
      });
      afterRenderEffect(async () => {
        if (!this.$el) return;
        const shouldAppear = this.isInitialMount && this.visible() && this.appear();
        const hideStrategy = this.hideStrategy();
        if (this.visible()) {
          await nextFrame();
          resetStyles(this.$el, hideStrategy);
          if (shouldAppear || !this.isInitialMount) {
            this.applyMotionDuration('enter');
            this.motion?.enter();
          }
        } else if (!this.isInitialMount) {
          await nextFrame();
          this.applyMotionDuration('leave');
          this.motion?.leave()?.then(async () => {
            if (this.$el && !this.cancelled && !this.visible()) {
              applyHiddenStyles(this.$el, hideStrategy);
              if (this.unmountOnLeave()) {
                await nextFrame();
                if (!this.cancelled) {
                  this.rendered.set(false);
                }
              }
            }
          });
        }
        this.isInitialMount = false;
      });
    }
    applyMotionDuration(phase) {
      const options = untracked(this.motionOptions);
      const ms = resolveDuration(options.duration, phase);
      if (ms == null || !this.$el) return;
      const el = this.$el;
      const durationValue = `${ms}ms`;
      if (options.type === 'transition') {
        el.style.transitionDuration = durationValue;
      } else {
        el.style.animationDuration = durationValue;
      }
    }
    onDestroy() {
      this.destroyed = true;
      this.cancelled = true;
      this.motion?.cancel();
      this.motion = undefined;
      resetStyles(this.$el, this.hideStrategy());
      this.$el?.remove();
      this.isInitialMount = true;
    }
    static ɵfac = function Motion_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || Motion)();
    };
    static ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: Motion,
      selectors: [["p-motion"]],
      hostVars: 2,
      hostBindings: function Motion_HostBindings(rf, ctx) {
        if (rf & 2) {
          i0.ɵɵclassMap(ctx.cx("root"));
        }
      },
      inputs: {
        visible: [1, "visible"],
        mountOnEnter: [1, "mountOnEnter"],
        unmountOnLeave: [1, "unmountOnLeave"],
        name: [1, "name"],
        type: [1, "type"],
        safe: [1, "safe"],
        disabled: [1, "disabled"],
        appear: [1, "appear"],
        enter: [1, "enter"],
        leave: [1, "leave"],
        duration: [1, "duration"],
        hideStrategy: [1, "hideStrategy"],
        enterFromClass: [1, "enterFromClass"],
        enterToClass: [1, "enterToClass"],
        enterActiveClass: [1, "enterActiveClass"],
        leaveFromClass: [1, "leaveFromClass"],
        leaveToClass: [1, "leaveToClass"],
        leaveActiveClass: [1, "leaveActiveClass"],
        options: [1, "options"]
      },
      outputs: {
        onBeforeEnter: "onBeforeEnter",
        onEnter: "onEnter",
        onAfterEnter: "onAfterEnter",
        onEnterCancelled: "onEnterCancelled",
        onBeforeLeave: "onBeforeLeave",
        onLeave: "onLeave",
        onAfterLeave: "onAfterLeave",
        onLeaveCancelled: "onLeaveCancelled"
      },
      features: [i0.ɵɵProvidersFeature([MotionStyle, {
        provide: MOTION_INSTANCE,
        useExisting: Motion
      }, {
        provide: PARENT_INSTANCE,
        useExisting: Motion
      }]), i0.ɵɵHostDirectivesFeature([i1.Bind]), i0.ɵɵInheritDefinitionFeature],
      ngContentSelectors: _c0,
      decls: 1,
      vars: 1,
      template: function Motion_Template(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵprojectionDef();
          i0.ɵɵconditionalCreate(0, Motion_Conditional_0_Template, 1, 0);
        }
        if (rf & 2) {
          i0.ɵɵconditional(ctx.rendered() ? 0 : -1);
        }
      },
      dependencies: [CommonModule, BindModule],
      encapsulation: 2
    });
  }
  return Motion;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const MOTION_DIRECTIVE_INSTANCE = /*#__PURE__*/new InjectionToken('MOTION_DIRECTIVE_INSTANCE');
/**
 * Motion Directive is directive to apply motion effects to elements.
 * @group Components
 */
let MotionDirective = /*#__PURE__*/(() => {
  class MotionDirective extends BaseComponent {
    $pcMotionDirective = inject(MOTION_DIRECTIVE_INSTANCE, {
      optional: true,
      skipSelf: true
    }) ?? undefined;
    /******************** Inputs ********************/
    /**
     * Whether the element is visible or not.
     * @group Props
     */
    visible = input(false, {
      ...(ngDevMode ? {
        debugName: "visible"
      } : {}),
      alias: 'pMotion'
    });
    /**
     * The name of the motion. It can be a predefined motion name or a custom one.
     * phases:
     *     [name]-enter
     *     [name]-enter-active
     *     [name]-enter-to
     *     [name]-leave
     *     [name]-leave-active
     *     [name]-leave-to
     * @group Props
     */
    name = input(undefined, {
      ...(ngDevMode ? {
        debugName: "name"
      } : {}),
      alias: 'pMotionName'
    });
    /**
     * The type of the motion, valid values 'transition' and 'animation'.
     * @group Props
     */
    type = input(undefined, {
      ...(ngDevMode ? {
        debugName: "type"
      } : {}),
      alias: 'pMotionType'
    });
    /**
     * Whether the motion is safe.
     * @group Props
     */
    safe = input(undefined, {
      ...(ngDevMode ? {
        debugName: "safe"
      } : {}),
      alias: 'pMotionSafe'
    });
    /**
     * Whether the motion is disabled.
     * @group Props
     */
    disabled = input(false, {
      ...(ngDevMode ? {
        debugName: "disabled"
      } : {}),
      alias: 'pMotionDisabled'
    });
    /**
     * Whether the motion should appear.
     * @group Props
     */
    appear = input(false, {
      ...(ngDevMode ? {
        debugName: "appear"
      } : {}),
      alias: 'pMotionAppear'
    });
    /**
     * Whether the motion should enter.
     * @group Props
     */
    enter = input(true, {
      ...(ngDevMode ? {
        debugName: "enter"
      } : {}),
      alias: 'pMotionEnter'
    });
    /**
     * Whether the motion should leave.
     * @group Props
     */
    leave = input(true, {
      ...(ngDevMode ? {
        debugName: "leave"
      } : {}),
      alias: 'pMotionLeave'
    });
    /**
     * The duration of the motion.
     * @group Props
     */
    duration = input(undefined, {
      ...(ngDevMode ? {
        debugName: "duration"
      } : {}),
      alias: 'pMotionDuration'
    });
    /**
     * The hide strategy of the motion, valid values 'display' and 'visibility'.
     * @group Props
     */
    hideStrategy = input('display', {
      ...(ngDevMode ? {
        debugName: "hideStrategy"
      } : {}),
      alias: 'pMotionHideStrategy'
    });
    /**
     * The enter from class of the motion.
     * @group Props
     */
    enterFromClass = input(undefined, {
      ...(ngDevMode ? {
        debugName: "enterFromClass"
      } : {}),
      alias: 'pMotionEnterFromClass'
    });
    /**
     * The enter to class of the motion.
     * @group Props
     */
    enterToClass = input(undefined, {
      ...(ngDevMode ? {
        debugName: "enterToClass"
      } : {}),
      alias: 'pMotionEnterToClass'
    });
    /**
     * The enter active class of the motion.
     * @group Props
     */
    enterActiveClass = input(undefined, {
      ...(ngDevMode ? {
        debugName: "enterActiveClass"
      } : {}),
      alias: 'pMotionEnterActiveClass'
    });
    /**
     * The leave from class of the motion.
     * @group Props
     */
    leaveFromClass = input(undefined, {
      ...(ngDevMode ? {
        debugName: "leaveFromClass"
      } : {}),
      alias: 'pMotionLeaveFromClass'
    });
    /**
     * The leave to class of the motion.
     * @group Props
     */
    leaveToClass = input(undefined, {
      ...(ngDevMode ? {
        debugName: "leaveToClass"
      } : {}),
      alias: 'pMotionLeaveToClass'
    });
    /**
     * The leave active class of the motion.
     * @group Props
     */
    leaveActiveClass = input(undefined, {
      ...(ngDevMode ? {
        debugName: "leaveActiveClass"
      } : {}),
      alias: 'pMotionLeaveActiveClass'
    });
    /******************** All Inputs ********************/
    /**
     * The motion options.
     * @group Props
     */
    options = input({}, {
      ...(ngDevMode ? {
        debugName: "options"
      } : {}),
      alias: 'pMotionOptions'
    });
    /******************** Outputs ********************/
    /**
     * Callback fired before the enter transition/animation starts.
     * @param {MotionEvent} [event] - The event object containing details about the motion.
     * @param {Element} event.element - The element being transitioned/animated.
     * @group Emits
     */
    onBeforeEnter = output({
      alias: 'pMotionOnBeforeEnter'
    });
    /**
     * Callback fired when the enter transition/animation starts.
     * @param {MotionEvent} [event] - The event object containing details about the motion.
     * @param {Element} event.element - The element being transitioned/animated.
     * @group Emits
     */
    onEnter = output({
      alias: 'pMotionOnEnter'
    });
    /**
     * Callback fired after the enter transition/animation ends.
     * @param {MotionEvent} [event] - The event object containing details about the motion.
     * @param {Element} event.element - The element being transitioned/animated.
     * @group Emits
     */
    onAfterEnter = output({
      alias: 'pMotionOnAfterEnter'
    });
    /**
     * Callback fired when the enter transition/animation is cancelled.
     * @param {MotionEvent} [event] - The event object containing details about the motion.
     * @param {Element} event.element - The element being transitioned/animated.
     * @group Emits
     */
    onEnterCancelled = output({
      alias: 'pMotionOnEnterCancelled'
    });
    /**
     * Callback fired before the leave transition/animation starts.
     * @param {MotionEvent} [event] - The event object containing details about the motion.
     * @param {Element} event.element - The element being transitioned/animated.
     * @group Emits
     */
    onBeforeLeave = output({
      alias: 'pMotionOnBeforeLeave'
    });
    /**
     * Callback fired when the leave transition/animation starts.
     * @param {MotionEvent} [event] - The event object containing details about the motion.
     * @param {Element} event.element - The element being transitioned/animated.
     * @group Emits
     */
    onLeave = output({
      alias: 'pMotionOnLeave'
    });
    /**
     * Callback fired after the leave transition/animation ends.
     * @param {MotionEvent} [event] - The event object containing details about the motion.
     * @param {Element} event.element - The element being transitioned/animated.
     * @group Emits
     */
    onAfterLeave = output({
      alias: 'pMotionOnAfterLeave'
    });
    /**
     * Callback fired when the leave transition/animation is cancelled.
     * @param {MotionEvent} [event] - The event object containing details about the motion.
     * @param {Element} event.element - The element being transitioned/animated.
     * @group Emits
     */
    onLeaveCancelled = output({
      alias: 'pMotionOnLeaveCancelled'
    });
    /******************** Computed ********************/
    motionOptions = computed(() => {
      const options = this.options() ?? {};
      return {
        name: options.name ?? this.name(),
        type: options.type ?? this.type(),
        safe: options.safe ?? this.safe(),
        disabled: options.disabled ?? this.disabled(),
        appear: false,
        enter: options.enter ?? this.enter(),
        leave: options.leave ?? this.leave(),
        duration: options.duration ?? this.duration(),
        enterClass: {
          from: options.enterClass?.from ?? (!options.name ? this.enterFromClass() : undefined),
          to: options.enterClass?.to ?? (!options.name ? this.enterToClass() : undefined),
          active: options.enterClass?.active ?? (!options.name ? this.enterActiveClass() : undefined)
        },
        leaveClass: {
          from: options.leaveClass?.from ?? (!options.name ? this.leaveFromClass() : undefined),
          to: options.leaveClass?.to ?? (!options.name ? this.leaveToClass() : undefined),
          active: options.leaveClass?.active ?? (!options.name ? this.leaveActiveClass() : undefined)
        },
        onBeforeEnter: options.onBeforeEnter ?? this.handleBeforeEnter,
        onEnter: options.onEnter ?? this.handleEnter,
        onAfterEnter: options.onAfterEnter ?? this.handleAfterEnter,
        onEnterCancelled: options.onEnterCancelled ?? this.handleEnterCancelled,
        onBeforeLeave: options.onBeforeLeave ?? this.handleBeforeLeave,
        onLeave: options.onLeave ?? this.handleLeave,
        onAfterLeave: options.onAfterLeave ?? this.handleAfterLeave,
        onLeaveCancelled: options.onLeaveCancelled ?? this.handleLeaveCancelled
      };
    }, ...(ngDevMode ? [{
      debugName: "motionOptions"
    }] : []));
    motion;
    isInitialMount = true;
    cancelled = false;
    destroyed = false;
    handleBeforeEnter = event => !this.destroyed && this.onBeforeEnter.emit(event);
    handleEnter = event => !this.destroyed && this.onEnter.emit(event);
    handleAfterEnter = event => !this.destroyed && this.onAfterEnter.emit(event);
    handleEnterCancelled = event => !this.destroyed && this.onEnterCancelled.emit(event);
    handleBeforeLeave = event => !this.destroyed && this.onBeforeLeave.emit(event);
    handleLeave = event => !this.destroyed && this.onLeave.emit(event);
    handleAfterLeave = event => !this.destroyed && this.onAfterLeave.emit(event);
    handleLeaveCancelled = event => !this.destroyed && this.onLeaveCancelled.emit(event);
    constructor() {
      super();
      effect(() => {
        if (!this.motion) {
          this.motion = createMotion(this.$el, this.motionOptions());
        } else {
          // @todo: Update motion options method to update options dynamically
          //this.motion.update(this.$el, this.motionOptions());
        }
      });
      afterRenderEffect(() => {
        if (!this.$el) return;
        const shouldAppear = this.isInitialMount && this.visible() && this.appear();
        const hideStrategy = this.hideStrategy();
        if (this.visible()) {
          resetStyles(this.$el, hideStrategy);
          if (shouldAppear || !this.isInitialMount) {
            this.applyMotionDuration('enter');
            this.motion?.enter();
          }
        } else if (!this.isInitialMount) {
          this.applyMotionDuration('leave');
          this.motion?.leave()?.then(() => {
            if (this.$el && !this.cancelled && !this.visible()) {
              applyHiddenStyles(this.$el, hideStrategy);
            }
          });
        } else {
          applyHiddenStyles(this.$el, hideStrategy);
        }
        this.isInitialMount = false;
      });
    }
    applyMotionDuration(phase) {
      const options = untracked(this.motionOptions);
      const ms = resolveDuration(options.duration, phase);
      if (ms == null || !this.$el) return;
      const el = this.$el;
      const durationValue = `${ms}ms`;
      if (options.type === 'transition') {
        el.style.transitionDuration = durationValue;
      } else {
        el.style.animationDuration = durationValue;
      }
    }
    onDestroy() {
      this.destroyed = true;
      this.cancelled = true;
      this.motion?.cancel();
      this.motion = undefined;
      resetStyles(this.$el, this.hideStrategy());
      this.$el?.remove();
      this.isInitialMount = true;
    }
    static ɵfac = function MotionDirective_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || MotionDirective)();
    };
    static ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
      type: MotionDirective,
      selectors: [["", "pMotion", ""]],
      inputs: {
        visible: [1, "pMotion", "visible"],
        name: [1, "pMotionName", "name"],
        type: [1, "pMotionType", "type"],
        safe: [1, "pMotionSafe", "safe"],
        disabled: [1, "pMotionDisabled", "disabled"],
        appear: [1, "pMotionAppear", "appear"],
        enter: [1, "pMotionEnter", "enter"],
        leave: [1, "pMotionLeave", "leave"],
        duration: [1, "pMotionDuration", "duration"],
        hideStrategy: [1, "pMotionHideStrategy", "hideStrategy"],
        enterFromClass: [1, "pMotionEnterFromClass", "enterFromClass"],
        enterToClass: [1, "pMotionEnterToClass", "enterToClass"],
        enterActiveClass: [1, "pMotionEnterActiveClass", "enterActiveClass"],
        leaveFromClass: [1, "pMotionLeaveFromClass", "leaveFromClass"],
        leaveToClass: [1, "pMotionLeaveToClass", "leaveToClass"],
        leaveActiveClass: [1, "pMotionLeaveActiveClass", "leaveActiveClass"],
        options: [1, "pMotionOptions", "options"]
      },
      outputs: {
        onBeforeEnter: "pMotionOnBeforeEnter",
        onEnter: "pMotionOnEnter",
        onAfterEnter: "pMotionOnAfterEnter",
        onEnterCancelled: "pMotionOnEnterCancelled",
        onBeforeLeave: "pMotionOnBeforeLeave",
        onLeave: "pMotionOnLeave",
        onAfterLeave: "pMotionOnAfterLeave",
        onLeaveCancelled: "pMotionOnLeaveCancelled"
      },
      features: [i0.ɵɵProvidersFeature([MotionStyle, {
        provide: MOTION_DIRECTIVE_INSTANCE,
        useExisting: MotionDirective
      }, {
        provide: PARENT_INSTANCE,
        useExisting: MotionDirective
      }]), i0.ɵɵInheritDefinitionFeature]
    });
  }
  return MotionDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let MotionModule = /*#__PURE__*/(() => {
  class MotionModule {
    static ɵfac = function MotionModule_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || MotionModule)();
    };
    static ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: MotionModule
    });
    static ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
      imports: [Motion]
    });
  }
  return MotionModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Generated bundle index. Do not edit.
 */

export { Motion, MotionDirective, MotionModule };
